import { useState } from "react";

import { CopyButton } from "~/components/copy-button";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { AdminApiError, fetchAdminLookup } from "~/lib/admin-api";
import { formatDateTime } from "~/lib/format";
import type { AdminLookupMatch, AdminLookupMatchKind } from "~/types/admin-status";

const kindLabels: Record<AdminLookupMatchKind, string> = {
  one_time_order: "注文",
  purchase_hold: "仮押さえ",
  cycle: "定期回",
};

function matchDisplayId(match: AdminLookupMatch): string {
  return match.order_id ?? match.hold_id ?? match.cycle_id ?? match.id;
}

export function PaymentLookupPanel({ accessToken }: { accessToken: string }) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<AdminLookupMatch[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const runSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed) {
      setError("検索文字列を入力してください。");
      setMatches(null);
      return;
    }

    setIsSearching(true);
    setError(null);
    try {
      const response = await fetchAdminLookup(accessToken, trimmed);
      setMatches(response.matches);
    } catch (caught) {
      setMatches(null);
      setError(
        caught instanceof AdminApiError
          ? caught.message
          : "決済照会に失敗しました。",
      );
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>決済照会</CardTitle>
        <CardDescription>
          pay.jp の決済 ID、または注文・仮押さえ・定期回の ID で突合します。
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void runSearch();
          }}
        >
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="pfw_… または注文/hold/cycle ID"
            className="max-w-md font-mono"
          />
          <Button type="submit" disabled={isSearching}>
            {isSearching ? "検索中…" : "検索"}
          </Button>
        </form>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>照会できませんでした</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {matches ? (
          matches.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              一致するデータが見つかりませんでした。
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>種別</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>状態</TableHead>
                  <TableHead>作成日時</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matches.map((match) => (
                  <TableRow key={`${match.kind}:${match.id}`}>
                    <TableCell>
                      <Badge variant="secondary">{kindLabels[match.kind]}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        <span className="font-mono" title={matchDisplayId(match)}>
                          {matchDisplayId(match)}
                        </span>
                        <CopyButton value={matchDisplayId(match)} />
                      </span>
                    </TableCell>
                    <TableCell title={match.status}>
                      {match.status}
                      {match.attempt_status ? (
                        <span className="block text-xs text-muted-foreground">
                          決済試行: {match.attempt_status}
                          {match.attempt_no !== undefined
                            ? `（#${match.attempt_no}）`
                            : ""}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>{formatDateTime(match.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}
